/**
 * Admin AI Models API Service
 *
 * Wraps the `/api/admin/ai-models` endpoints used by the admin AI Models
 * page, the Active Models panel on the Content Generation workspace,
 * and the cross-page "new models discovered" banner.
 *
 * Why a single service file (and not split per endpoint): all four UI
 * surfaces share the same enum definitions and request/response shapes,
 * so colocating them keeps the type contract honest and avoids drift
 * when a new field is added to AiModelCatalogEntryDto.
 */
import apiClient from './apiClient';

/**
 * Vendor enum mirrored from the backend `AiModelVendor` enum.
 *
 * Values are the C# enum NAMES (not the underlying integers) because the
 * API has `JsonStringEnumConverter` registered globally in `Program.cs`
 * — every AI Model endpoint emits `vendor: "OpenAi"` on the wire, never
 * `vendor: 0`. A previous version of this file pinned numeric values
 * to "match the .NET enum" which silently broke every `===` comparison
 * on the page (every dropdown showed "No selection" because
 * `"OpenAi" === 0` is always false). The string values below match what
 * the API actually returns and what the controller's `[FromQuery]` enum
 * binders accept.
 */
export enum AiModelVendor {
  OpenAi = 'OpenAi',
  Runway = 'Runway',
}

/**
 * Capability enum mirrored from the backend `AiModelCapability` enum.
 * String values per the JsonStringEnumConverter contract (see the
 * `AiModelVendor` doc comment for the full rationale).
 */
export enum AiModelCapability {
  Image = 'Image',
  Text = 'Text',
  Video = 'Video',
}

/**
 * Class enum mirrored from the backend `AiModelClass` enum. `Default`
 * applies to OpenAI rows; `Fast` and `HighQuality` apply to Runway rows.
 * String values per the JsonStringEnumConverter contract.
 */
export enum AiModelClass {
  Default = 'Default',
  Fast = 'Fast',
  HighQuality = 'HighQuality',
}

/**
 * Lifecycle enum mirrored from the backend `AiModelStatus` enum.
 *
 * `CurrentlySelected` matches the filtered unique index — exactly one
 * row per `(Vendor, Capability, Class)` bucket can be flagged at a time.
 * String values per the JsonStringEnumConverter contract.
 */
export enum AiModelStatus {
  Available = 'Available',
  CurrentlySelected = 'CurrentlySelected',
  Hidden = 'Hidden',
}

export enum AiModelProbeStatus {
  Unknown = 'Unknown',
  Passed = 'Passed',
  Failed = 'Failed',
  Inconclusive = 'Inconclusive',
}

/**
 * Read-only projection of an AI Model Catalog row returned by the
 * listing endpoints. `metadataJson` is opaque to this service — the
 * Configure modal deserializes it client-side based on `capability`.
 */
export interface AiModelCatalogEntry {
  id: string;
  slug: string;
  vendor: AiModelVendor;
  capability: AiModelCapability;
  class: AiModelClass;
  externalId: string;
  status: AiModelStatus;
  metadataJson: string;
  createdAtVendor: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  lastSelectedAt: string | null;
  lastSelectedByUserId: string | null;
  shutdownDateUtc: string | null;
  retiredAtUtc: string | null;
  fallbackPriority: number | null;
  probeStatus: AiModelProbeStatus;
  supportsVision: boolean;
  inputPricePerMTok: number | null;
  outputPricePerMTok: number | null;
  priceSource: string | null;
  priceAsOfUtc?: string | null;
  communityDeprecationDateUtc?: string | null;
}

export interface AiModelTextOperation {
  operation: string;
  displayName: string;
  group: string;
  requiresVision: boolean;
  requiresLogprobs: boolean;
  maxOutputTokens: number;
  catalogEntryId: string | null;
  externalId: string | null;
  shelfClass: AiModelClass | null;
  probeStatus: AiModelProbeStatus | null;
  inputPricePerMTok: number | null;
  outputPricePerMTok: number | null;
  priceSource: string | null;
  priceAsOfUtc: string | null;
  communityDeprecationDateUtc: string | null;
}

/**
 * Banner / discoveries panel summary returned by
 * `GET /admin/ai-models/discoveries`. Drives both the cross-page
 * NewModelsBanner and the discoveries section on the AI Models page.
 */
export interface AiModelDiscoverySummary {
  latestDiscoveryAtUtc: string | null;
  discoveredCount: number;
  discoveredExternalIds: string[];
}

/**
 * Per-run summary returned by the on-demand sync endpoint. Mirrors the
 * backend `OpenAiModelDiscoveryResult` record so admin UI can show "5
 * new models, 240ms" style feedback after pressing "Sync now".
 */
export interface OpenAiModelDiscoveryResult {
  totalSeen: number;
  newlyDiscovered: Array<{
    slug: string;
    externalId: string;
    capability: AiModelCapability;
    createdAtVendor: string | null;
  }>;
  filteredOut: number;
  durationMs: number;
  errorMessage: string | null;
}

/**
 * Filters accepted by `GET /admin/ai-models`.
 */
export interface ListAiModelsParams {
  vendor?: AiModelVendor;
  capability?: AiModelCapability;
  class?: AiModelClass;
  includeHidden?: boolean;
}

/**
 * Lists catalog rows. Use the filters to scope a single card on the
 * AI Models page (e.g. OpenAI image dropdown).
 */
export const listAiModels = async (params: ListAiModelsParams = {}): Promise<AiModelCatalogEntry[]> => {
  const response = await apiClient.get<AiModelCatalogEntry[]>('/admin/ai-models', { params });
  return response.data;
};

/**
 * Returns just the rows currently flagged `CurrentlySelected`. Drives
 * the Active Models panel on the Content Generation workspace and the
 * AiModelHint badges across the workspace.
 */
export const listCurrentAiModels = async (): Promise<AiModelCatalogEntry[]> => {
  const response = await apiClient.get<AiModelCatalogEntry[]>('/admin/ai-models/current');
  return response.data;
};

/**
 * Fetches the calling admin's undismissed discovery summary.
 */
export const getAiModelDiscoveries = async (): Promise<AiModelDiscoverySummary> => {
  const response = await apiClient.get<AiModelDiscoverySummary>('/admin/ai-models/discoveries');
  return response.data;
};

/**
 * Marks every current discovery as dismissed for the calling admin.
 * Per-admin storage means dismissing on one device hides the banner on
 * every device for the same admin — see backend
 * `User.LastAiModelBannerDismissedAt`.
 */
export const dismissAiModelDiscoveries = async (): Promise<void> => {
  await apiClient.post('/admin/ai-models/discoveries/dismiss');
};

/**
 * Forces an on-demand OpenAI sync. Backs the "Sync now" admin button.
 */
export const runAiModelSync = async (): Promise<OpenAiModelDiscoveryResult> => {
  const response = await apiClient.post<OpenAiModelDiscoveryResult>('/admin/ai-models/sync');
  return response.data;
};

/**
 * Promotes a model to `CurrentlySelected` and atomically commits any
 * metadata edits made in the Configure modal. The previously-selected
 * row in the same `(Vendor, Capability, Class)` bucket is demoted to
 * `Available` server-side.
 */
export const selectAiModel = async (id: string, metadataJson: string): Promise<AiModelCatalogEntry> => {
  const response = await apiClient.put<AiModelCatalogEntry>(`/admin/ai-models/${id}/select`, {
    metadataJson,
  });
  return response.data;
};

/**
 * Hides a row from the dropdowns. Cannot be applied to the currently-
 * selected row — backend returns 409.
 */
export const hideAiModel = async (id: string): Promise<AiModelCatalogEntry> => {
  const response = await apiClient.put<AiModelCatalogEntry>(`/admin/ai-models/${id}/hide`);
  return response.data;
};

/**
 * Restores a previously-hidden row to `Available`. Does NOT promote to
 * `CurrentlySelected` — admin must run a follow-up select call.
 */
export const unhideAiModel = async (id: string): Promise<AiModelCatalogEntry> => {
  const response = await apiClient.put<AiModelCatalogEntry>(`/admin/ai-models/${id}/unhide`);
  return response.data;
};

/**
 * Manually adds a Runway video model. Runway has no `/v1/models`
 * endpoint so admin-entered rows are the only way new Runway versions
 * enter the catalog.
 */
export interface AiModelRecommendation {
  id: string;
  vendor: AiModelVendor;
  capability: AiModelCapability;
  class: AiModelClass;
  recommendedExternalId: string;
  reason: string;
  inputPricePerMTok: number | null;
  outputPricePerMTok: number | null;
  recommendedAtUtc: string;
  probeValidated: boolean;
}

export const listTextOperations = async (): Promise<AiModelTextOperation[]> => {
  const response = await apiClient.get<AiModelTextOperation[]>('/admin/ai-models/text-operations');
  return response.data;
};

export const updateTextOperation = async (
  operation: string,
  catalogEntryId: string,
): Promise<void> => {
  await apiClient.put(`/admin/ai-models/text-operations/${encodeURIComponent(operation)}`, {
    catalogEntryId,
  });
};

export const updateTextModelPricing = async (
  id: string,
  payload: { inputPricePerMTok: number | null; outputPricePerMTok: number | null; resetToPriceList: boolean },
): Promise<AiModelCatalogEntry> => {
  const response = await apiClient.put<AiModelCatalogEntry>(`/admin/ai-models/${id}/text-pricing`, {
    inputPricePerMTok: payload.inputPricePerMTok,
    outputPricePerMTok: payload.outputPricePerMTok,
    resetToPriceList: payload.resetToPriceList,
  });
  return response.data;
};

export const updateFallbackPriorities = async (
  updates: Array<{ id: string; fallbackPriority: number | null }>,
): Promise<void> => {
  await apiClient.put('/admin/ai-models/fallback-priorities', updates);
};

export const listAiModelRecommendations = async (): Promise<AiModelRecommendation[]> => {
  const response = await apiClient.get<AiModelRecommendation[]>('/admin/ai-models/recommendations');
  return response.data;
};

export const approveAiModelRecommendation = async (id: string): Promise<AiModelCatalogEntry> => {
  const response = await apiClient.post<AiModelCatalogEntry>(
    `/admin/ai-models/recommendations/${id}/approve`,
  );
  return response.data;
};

export const dismissAiModelRecommendation = async (id: string): Promise<void> => {
  await apiClient.post(`/admin/ai-models/recommendations/${id}/dismiss`);
};

export interface AiModelPromotionRecord {
  id: string;
  vendor: AiModelVendor;
  capability: AiModelCapability;
  class: AiModelClass;
  previousExternalId: string;
  newExternalId: string;
  reason: string;
  promotedAtUtc: string;
  rolledBack: boolean;
  rolledBackAtUtc: string | null;
}

export const listAiModelPromotions = async (): Promise<AiModelPromotionRecord[]> => {
  const response = await apiClient.get<AiModelPromotionRecord[]>('/admin/ai-models/promotions');
  return response.data;
};

export const rollbackAiModelPromotion = async (id: string): Promise<AiModelCatalogEntry> => {
  const response = await apiClient.post<AiModelCatalogEntry>(
    `/admin/ai-models/promotions/${id}/rollback`,
  );
  return response.data;
};

export const updateAiModelClass = async (
  id: string,
  modelClass: AiModelClass,
): Promise<AiModelCatalogEntry> => {
  const response = await apiClient.put<AiModelCatalogEntry>(`/admin/ai-models/${id}/class`, {
    class: modelClass,
  });
  return response.data;
};

export const addRunwayAiModel = async (
  externalId: string,
  modelClass: AiModelClass,
  metadataJson: string,
): Promise<AiModelCatalogEntry> => {
  const response = await apiClient.post<AiModelCatalogEntry>('/admin/ai-models/runway', {
    externalId,
    class: modelClass,
    metadataJson,
  });
  return response.data;
};
